// sceneVisualService — the studio half of the Scene Visual Lab.
//
// Two tabs of "Görsel Ekle" are generative but produce an HTML document
// instead of an image: 3B Obje and Hareketli Görsel. Both speak to the same
// middleware shape as the infographic tabs — POST FormData, read { jobId },
// poll until the worker finishes — so the differences between them live in
// one place rather than in two near-identical components.
//
// What comes back is not a url but a READY BOX: { box, toolbar, boxId } the
// editor drops onto the scene when the author approves it. The backend knows
// which plugin its own output belongs in (MotionScene for motion, Webpage for
// a 3D object); the studio does not have to guess from a file extension.

import { getToken } from "../../../common/environment_config.es6";
import { waitForJob } from "../../../common/job_poll.es6";

/** The two kinds this module can produce, and the routes behind them. */
export const SCENE_VISUAL_KINDS = { THREE_D: "3d", MOTION: "motion" };

const ROUTE = {
  [SCENE_VISUAL_KINDS.THREE_D]: "3d",
  [SCENE_VISUAL_KINDS.MOTION]: "motion",
};

/**
 * The model picker. "" means "let the backend choose" — it does NOT mean a
 * model id pinned here, deliberately: the default lives in one place
 * (DEFAULT_SCENE_VISUAL_MODEL) and a copy in the studio would drift from it
 * and, because the price is per model, quietly bill against the wrong row.
 */
export const SCENE_VISUAL_MODELS = [
  { value: "", labelKey: "SceneVisual.ModelAuto", fallback: "Otomatik (Sonnet 5.5)" },
  { value: "claude-sonnet-5.5", labelKey: "SceneVisual.ModelSonnet", fallback: "Sonnet 5.5" },
  { value: "claude-opus-5.5", labelKey: "SceneVisual.ModelOpus", fallback: "Opus 5.5" },
  {
    value: "deepseek-v4.1-flash",
    labelKey: "SceneVisual.ModelDeepseekFlash",
    fallback: "DeepSeek V4.1 Flash",
  },
];

function apiBase() {
  const base = process.env.MW_API_URL || "";
  return base.endsWith("/") ? base : `${base}/`;
}

/**
 * The editor's ?id= is the Ideal Studio training UUID — the socket room the
 * job widget joined and the blob scope the document is written under. Absent
 * in standalone mode, where the document simply lands in the org's temp scope.
 */
function roomTrainingId() {
  try {
    return new URLSearchParams(window.location.search).get("id") || null;
  } catch {
    return null;
  }
}

function buildForm(fields) {
  const form = new FormData();
  const put = (key, value) => {
    if (value === undefined || value === null || value === "") return;
    form.append(key, String(value));
  };

  put("prompt", fields.prompt);
  put("narrationText", fields.narrationText);
  put("navId", fields.navId);
  put("sceneTitle", fields.sceneTitle);
  put("styleBrief", fields.styleBrief);
  put("contentLanguage", fields.contentLanguage);
  put("sceneIndex", fields.sceneIndex);
  put("llmModel", fields.llmModel);
  put("orientation", fields.orientation);
  // Left out entirely when the author did not ask for a length: the model
  // decides it from the content, and an empty string would reach the DTO as
  // NaN rather than as "no opinion".
  put("durationSec", fields.durationSec);
  // "motion" (default), "gif" or "webp" — the last two render the same looping
  // design as an animated image in that format.
  put("output", fields.output);
  put("trainingId", fields.trainingId);
  // "AI ile düzenle": the sentence and the document it applies to.
  put("instruction", fields.instruction);
  put("sourceHtmlUrl", fields.sourceHtmlUrl);
  // "Çevir": "mp4" (varsayılan) ya da bir kere oynayan "webp".
  put("format", fields.format);

  const roomId = roomTrainingId();
  if (roomId) form.append("idealStudioTrainingId", roomId);

  return form;
}

async function post(path, fields) {
  const response = await fetch(`${apiBase()}api/scene-visual/${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: buildForm(fields),
  });
  if (!response.ok) {
    const errJson = await response.json().catch(() => null);
    const error = new Error(errJson?.message || errJson?.error || `HTTP ${response.status}`);
    // Carried so the caller can route an insufficient-balance refusal to the
    // credit modal instead of showing it as a generic failure.
    error.data = errJson;
    throw error;
  }
  return response.json();
}

/**
 * Start a generation and wait for the worker.
 *
 * 20 minutes, matching the queue's own timeout: a brief call plus one
 * high-reasoning write, and a repair turn when the static check rejects the
 * first one. A shorter wait here would abandon jobs that are still working —
 * and still being paid for. The "MP4" output also renders the scene in the
 * same job, so its queue timeout (and this wait) is 30 minutes.
 */
export async function generateSceneVisual(kind, fields) {
  const { jobId } = await post(ROUTE[kind], fields);
  if (!jobId) throw new Error("no-job");
  const timeoutMs = fields && fields.output === "mp4" ? 1_800_000 : 1_200_000;
  const result = await waitForJob(jobId, { timeoutMs });
  return { ...result, jobId };
}

/** What this click will cost, and what the user has left to spend. */
export async function estimateSceneVisual(kind, fields) {
  return post(`${ROUTE[kind]}-credit`, fields);
}

/**
 * "AI ile düzenle" on a motion box, or on a GIF/WebP this tab made.
 *
 * A sentence and the document — no frame, no playhead second. Same queue,
 * same wait and same price as a generation; the result has the same shape
 * (a new htmlUrl, plus imageUrl for gif/webp). The old files stay, so the
 * editor's undo takes the edit back.
 */
export async function editSceneMotion(fields) {
  const { jobId } = await post("motion/edit", fields);
  if (!jobId) throw new Error("no-job");
  const result = await waitForJob(jobId, { timeoutMs: 1_200_000 });
  return { ...result, jobId };
}

/**
 * "Çevir" on a motion box: the document rendered once, as it is — its own
 * length, no model, no charge. `format: "mp4"` resolves with
 * { kind: "video", videoUrl }, `format: "webp"` with { kind: "image",
 * imageUrl } (plays once, holds its last frame). Same wait as a generation:
 * a 90 s motion is 2700 frames to render.
 */
export async function convertMotionToVideo(fields) {
  const { jobId } = await post("motion/video", fields);
  if (!jobId) throw new Error("no-job");
  const result = await waitForJob(jobId, { timeoutMs: 1_200_000 });
  return { ...result, jobId };
}

/**
 * What the pencil edits on a box, or null when it has nothing to edit.
 *
 *   MotionScene   → its own document (state.htmlUrl).
 *   image (GIF/WebP from Hareketli Görsel) → state.motionSource, written when
 *                   the image was inserted; ordinary images have none.
 */
export function motionEditTarget(toolbar) {
  const state = (toolbar && toolbar.state) || {};
  if (toolbar && toolbar.pluginId === "MotionScene" && state.htmlUrl) {
    return {
      kind: "motion",
      sourceHtmlUrl: state.htmlUrl,
      output: "motion",
      orientation: state.orientation,
      llmModel: pickerModel(state.model),
    };
  }
  const src = state.motionSource;
  // imageUrl ≠ url: the file was replaced since ("Replace File"), and the
  // document no longer describes what the box shows.
  if (
    src &&
    src.htmlUrl &&
    (src.format === "gif" || src.format === "webp") &&
    (!src.imageUrl || src.imageUrl === state.url)
  ) {
    return {
      kind: "loop",
      sourceHtmlUrl: src.htmlUrl,
      output: src.format,
      orientation: src.orientation,
      llmModel: pickerModel(src.model),
    };
  }
  return null;
}

/**
 * A library row → the `motionSource` note, for a GIF/WebP Hareketli Görsel
 * made. The backend writes the document's address on the row's metadata
 * (scene-visual-file.registry), so an image re-added from the library keeps
 * its pencil. `url` is what the box will actually hold — the note's imageUrl
 * must match it, or motionEditTarget hides the pencil.
 *
 * Rows from before that field existed carry nothing to edit: undefined.
 */
export function motionSourceFromFileRow(row, url) {
  const md = (row && row.metadata) || {};
  if (md.source !== "scene-visual" || md.kind !== "motion-loop" || !md.sourceHtmlUrl) {
    return undefined;
  }
  const format =
    md.loopFormat ||
    String((row && row.fileExtension) || "")
      .replace(/^\./, "")
      .toLowerCase();
  if (format !== "gif" && format !== "webp") return undefined;
  return {
    htmlUrl: md.sourceHtmlUrl,
    format,
    orientation: md.orientation,
    model: row.model,
    imageUrl: url,
  };
}

// The box remembers the model that wrote it, but a motionize box may carry one
// this tab does not offer — the backend rejects (and has no price for) those,
// so anything off the picker falls back to the backend default.
function pickerModel(model) {
  return SCENE_VISUAL_MODELS.some((option) => option.value && option.value === model)
    ? model
    : undefined;
}
